import axios from "axios";
import { useAlertStore } from "@/stores/alertStore";

/**
 * API Client utility that handles axios requests with automatic alert management
 * Extracts success/error handling and provides consistent user feedback
 */
class ApiClient {
  constructor() {
    this.axios = axios;
  }

  /**
   * Extract meaningful error message from axios error response
   * @private
   */
  _extractErrorMessage(error) {
    if (error.response?.data) {
      const data = error.response.data;
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          return data.errors.join(", ");
        } else if (typeof data.errors === "object") {
          return Object.keys(data.errors)
            .map((field) => `${field}: ${data.errors[field].join(", ")}`)
            .join("; ");
        }
      }
    }
    return error.message || "Er is een onbekende fout opgetreden";
  }

  /**
   * Extract success message from response
   * @private
   */
  _extractSuccessMessage(response) {
    if (response.data?.message) {
      return response.data.message;
    }
    return null;
  }

  /**
   * Make API request with automatic alert handling
   * @param {Object} config - Axios request configuration
   * @param {Object} options - Additional options
   * @param {Boolean} options.showSuccessAlert - Show success alert (default: false for GET, true for others)
   * @param {String} options.successMessage - Custom success message
   * @param {Boolean} options.showErrorAlert - Show error alert (default: true)
   * @param {Boolean} options.returnFullResponse - Return full response instead of just data (default: false)
   */
  async request(config, options = {}) {
    const {
      showSuccessAlert = ["POST", "PATCH", "PUT", "DELETE"].includes(
        config.method?.toUpperCase(),
      ),
      successMessage,
      showErrorAlert = true,
      returnFullResponse = false,
    } = options;

    try {
      // Add default timeout if not specified
      if (!config.timeout) {
        config.timeout = 30000; // 30 second timeout
      }

      console.log("ApiClient: Starting axios request", {
        method: config.method,
        url: config.url,
        timeout: config.timeout,
        timestamp: new Date().toISOString(),
      });

      const response = await this.axios(config);

      console.log("ApiClient: Axios request completed", {
        method: config.method,
        url: config.url,
        status: response.status,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      // Handle success alerts
      if (showSuccessAlert) {
        const alertStore = useAlertStore();
        const message =
          successMessage ||
          this._extractSuccessMessage(response) ||
          this._getDefaultSuccessMessage(config.method);

        if (message) {
          alertStore.success(message);
        }
      }

      return returnFullResponse ? response : response.data;
    } catch (error) {
      console.error("ApiClient: Axios request failed", {
        method: config.method,
        url: config.url,
        errorMessage: error.message,
        hasResponse: !!error.response,
        responseStatus: error.response?.status,
        timestamp: new Date().toISOString(),
      });

      // 🔒 SECURITY: Handle 401 Unauthorized - clear token and let router handle redirect
      if (error.response?.status === 401) {
        console.warn(
          "ApiClient: 401 Unauthorized - clearing token, router will handle redirect",
        );

        // Clear token and headers - router guard will detect missing token and handle the rest
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
        if (this.axios?.defaults?.headers?.common) {
          delete this.axios.defaults.headers.common["Authorization"];
        }

        // Don't show error alert for 401
        throw error;
      }

      // Handle error alerts for non-401 errors
      if (showErrorAlert) {
        const alertStore = useAlertStore();
        const errorMessage = this._extractErrorMessage(error);
        alertStore.error(errorMessage);
      }

      throw error;
    }
  }

  /**
   * Get default success message based on HTTP method
   * @private
   */
  _getDefaultSuccessMessage(method) {
    const messages = {
      POST: "Record succesvol aangemaakt",
      PATCH: "Record succesvol bijgewerkt",
      PUT: "Record succesvol bijgewerkt",
      DELETE: "Record succesvol verwijderd",
    };
    return messages[method?.toUpperCase()];
  }

  /**
   * GET request - typically no success alerts
   */
  async get(url, config = {}, options = {}) {
    return this.request(
      {
        method: "GET",
        url,
        ...config,
      },
      {
        showSuccessAlert: false,
        ...options,
      },
    );
  }

  /**
   * POST request - shows success alert by default
   */
  async post(url, data, config = {}, options = {}) {
    return this.request(
      {
        method: "POST",
        url,
        data,
        ...config,
      },
      options,
    );
  }

  /**
   * PATCH request - shows success alert by default
   */
  async patch(url, data, config = {}, options = {}) {
    return this.request(
      {
        method: "PATCH",
        url,
        data,
        ...config,
      },
      options,
    );
  }

  /**
   * PUT request - shows success alert by default
   */
  async put(url, data, config = {}, options = {}) {
    return this.request(
      {
        method: "PUT",
        url,
        data,
        ...config,
      },
      options,
    );
  }

  /**
   * DELETE request - shows success alert by default
   */
  async delete(url, config = {}, options = {}) {
    return this.request(
      {
        method: "DELETE",
        url,
        ...config,
      },
      options,
    );
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
